/**
 * Слой напольных покрытий комнат.
 */
import { create } from "zustand";
import {
	getFloorPreset,
	resolveFloorLayout,
	type FloorLayout,
	type FloorMaterial,
} from "@/shared/catalog";
import type { RoomFloor } from "@/shared/plan";

interface FloorsLayerState {
	items: Record<string, RoomFloor>;
	visible: boolean;

	setRoomFloorMaterial: (
		roomId: string,
		material: FloorMaterial,
		layout?: FloorLayout,
		settings?: Partial<Omit<RoomFloor, "id" | "roomId" | "material" | "layout">>,
	) => void;
	removeRoomFloor: (roomId: string) => void;

	setVisible: (visible: boolean) => void;
	toggleVisible: () => void;

	restoreFloors: (roomFloors: Record<string, RoomFloor>) => void;
}

export const useFloorsStore = create<FloorsLayerState>((set) => ({
	items: {},
	visible: false,

	setRoomFloorMaterial: (
		roomId: string,
		material: FloorMaterial,
		layout?: FloorLayout,
		settings?: Partial<Omit<RoomFloor, "id" | "roomId" | "material" | "layout">>,
	) =>
		set((s) => {
			const current = s.items[roomId];
			const materialChanged = current?.material !== material;

			// Дефолтные значения материала — из каталога
			const preset = getFloorPreset(material);

			// Укладку разрешает каталог (`allowedLayouts` покрытия)
			const nextLayout = resolveFloorLayout(material, layout ?? current?.layout);
			const MAX_ANGLE = 360;

			// Картинка покрытия: передали — ставим (undefined снимает), сменили материал —
			// снимаем, иначе остаётся прежняя
			const texture =
				settings && "texture" in settings
					? settings.texture
					: materialChanged
						? undefined
						: current?.texture;
			const roomFloors = {
				...s.items,
				[roomId]: {
					id: roomId,
					roomId,
					material,
					layout: nextLayout,
					length:
						settings?.length ??
						(materialChanged ? preset.length : (current?.length ?? preset.length)),
					width:
						settings?.width ?? (materialChanged ? preset.width : (current?.width ?? preset.width)),
					gap: settings?.gap ?? (materialChanged ? preset.gap : (current?.gap ?? preset.gap)),
					tileColor:
						settings?.tileColor ??
						(materialChanged ? preset.tileColor : (current?.tileColor ?? preset.tileColor)),
					seamColor:
						settings?.seamColor ??
						(materialChanged ? preset.seamColor : (current?.seamColor ?? preset.seamColor)),
					raise:
						settings?.raise ?? (materialChanged ? preset.raise : (current?.raise ?? preset.raise)),
					rotation:
						(((settings?.rotation ??
							(materialChanged ? preset.rotation : (current?.rotation ?? preset.rotation)) ??
							0) %
							MAX_ANGLE) +
							MAX_ANGLE) %
						MAX_ANGLE,
					offsetX: settings?.offsetX ?? (materialChanged ? 0 : (current?.offsetX ?? 0)),
					offsetY: settings?.offsetY ?? (materialChanged ? 0 : (current?.offsetY ?? 0)),
					...(texture && { texture }),
				},
			};
			return { items: roomFloors };
		}),

	removeRoomFloor: (roomId) =>
		set((s) => {
			if (!s.items[roomId]) return s;
			const roomFloors = { ...s.items };
			delete roomFloors[roomId];
			return { items: roomFloors };
		}),

	setVisible: (visible) => set({ visible }),
	toggleVisible: () => set((s) => ({ visible: !s.visible })),

	restoreFloors: (roomFloors) => set({ items: roomFloors }),
}));
